/**
 * CurrentClinicBanner — shows which clinic the user is currently "in".
 *
 * Session 18 introduced the "current clinic" concept (see store/clinicSlice).
 * Session 18.1 extracts this banner into a shared component because it now
 * appears on more than one page (the Dashboard and the Services list), per
 * Person A's request that "you are currently at {clinic}" be visible while
 * browsing a clinic's services too.
 *
 * Self-contained: reads the current clinic + language from Redux and handles
 * its own navigation to the clinic-entry page, so callers just drop in
 * `<CurrentClinicBanner />` with no props.
 *
 * Two states:
 *   - A clinic is selected → "You are currently at {clinic}" + a small
 *     "Change clinic" button (→ /clinic-entry).
 *   - No clinic selected (came via "Personal panel") → a gentle prompt with
 *     an "Enter a clinic" button (→ /clinic-entry).
 *
 * The clinic name is bilingual; `localized()` picks the active language so it
 * flips instantly with the language toggle.
 */

import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { alpha } from '@mui/material/styles';
import { Paper, Stack, Box, Typography, Button } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

import { selectCurrentClinic } from '@/store/clinicSlice';
import { selectLanguage } from '@/store/uiSlice';
import { localized } from '@/utils/localizedContent';

export function CurrentClinicBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentClinic = useSelector(selectCurrentClinic);
  const language = useSelector(selectLanguage);

  const hasClinic = currentClinic != null;
  const clinicName = hasClinic
    ? localized(currentClinic.name, language) || currentClinic.code
    : '';

  const goToClinicEntry = () => navigate('/clinic-entry');

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: hasClinic
          ? (theme) => alpha(theme.palette.primary.main, 0.08)
          : 'background.paper',
        borderColor: hasClinic ? 'primary.light' : 'divider',
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <StorefrontIcon color="primary" />
          <Box sx={{ minWidth: 0 }}>
            {hasClinic ? (
              <>
                <Typography variant="caption" color="text.secondary" component="div">
                  {t('dashboard.currentClinic.label')}
                </Typography>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {clinicName}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('dashboard.currentClinic.none')}
              </Typography>
            )}
          </Box>
        </Stack>

        <Button
          size="small"
          variant={hasClinic ? 'text' : 'contained'}
          startIcon={
            hasClinic ? (
              <SwapHorizIcon fontSize="small" />
            ) : (
              <StorefrontIcon fontSize="small" />
            )
          }
          onClick={goToClinicEntry}
          sx={{ flexShrink: 0 }}
        >
          {hasClinic
            ? t('dashboard.currentClinic.change')
            : t('dashboard.currentClinic.enter')}
        </Button>
      </Stack>
    </Paper>
  );
}

export default CurrentClinicBanner;
